import { supertypeClass, property } from '@haventech/amorphic';
import { Income } from './Income';
import { EarnedIncome } from './EarnedIncome';
import { Applicant } from '../customer/Applicant';

@supertypeClass({ toClient: true, toServer: true })
export class SSN extends Income {
  @property()
  earnedIncome: EarnedIncome;

  @property()
  applicant: Applicant;

  @property()
  calculationDetails: {};

  constructor() {
    super();
  }

  static new(earnedIncome, applicant) {
    return new SSN().init(earnedIncome, applicant);
  }

  init(earnedIncome?, applicant?) {
    super.init(earnedIncome ? earnedIncome.customer : null, 0, 'ssn');

    this.applicant = applicant;
    this.earnedIncome = earnedIncome;
    this.calculationDetails = {};
    this.maxGroup = 'SSN';
    return this;
  }

  isActive() {
    return this.earnedIncome.amount > 0;
  }

  getStartingFlow(deceased, yearOfDeath) {
    /*
         This is my best guess at how to calculate this based on the various SSA web site pages
         There are some assumptions about inflating bend points and the index and ratios used
         to compute PIA that all assume an SSN COLA rate
         */
    // Earned income for survivor that will reduce family benefit
    const survivorEarnedIncome =
      deceased === this.customer.primaryCustomer
        ? this.customer.capitalNeeds.earnedIncome[1]
        : this.customer.capitalNeeds.earnedIncome[0];

    // http://www.ssa.gov/pubs/10070.html#a0=0 and http://www.ssa.gov/oact/cola/Benefits.html
    const age = this.earnedIncome.applicant.person.age;
    const retirementAge = this.customer.settings.retirementAge;
    const earningsHistory = [];

    // Bend points for benefit and max calculation http://www.ssa.gov/oact/cola/bendpoints.html
    const PIABendPoints = [];

    for (let ix = 0; ix < this.customer.assumptions.PIABendPoints.length; ++ix) {
      PIABendPoints[ix] = this.customer.assumptions.PIABendPoints[ix];
    }

    const FMBendPoints = [];

    for (let ix = 0; ix < this.customer.assumptions.FMBendPoints.length; ++ix) {
      FMBendPoints[ix] = this.customer.assumptions.FMBendPoints[ix];
    }

    // Inflate them in case we don't update the table in subsequent years
    let year = this.customer.assumptions.bendPointYear;

    while (year++ < new Date().getFullYear()) {
      this.inflateBendPoints(PIABendPoints);
      this.inflateBendPoints(FMBendPoints);
    }

    // Go back in time using salary increase rate and compute as per http://www.ssa.gov/pubs/10070.html#a0=0
    let earnings = this.earnedIncome.amount;
    let currentYear = new Date().getFullYear();
    const firstYearWorking = currentYear - this.earnedIncome.yearsWorked + yearOfDeath;

    while (currentYear-- >= firstYearWorking) {
      earningsHistory.splice(
        0,
        0,
        Math.min(this.customer.assumptions.ssnTable[currentYear][0], earnings) *
          this.customer.assumptions.ssnTable[currentYear][1]
      );
      earnings = earnings - earnings * this.customer.settings.inflation;
    }

    // Go forward to the year of death
    earnings = this.earnedIncome.amount;

    for (
      currentYear = currentYear = new Date().getFullYear();
      currentYear < new Date().getFullYear() + yearOfDeath;
      ++currentYear
    ) {
      earningsHistory.push(
        Math.min(this.customer.assumptions.ssnTable[currentYear][0], earnings) *
          this.customer.assumptions.ssnTable[currentYear][1]
      );
      earnings = earnings + earnings * this.customer.settings.inflation;
      this.inflateBendPoints(PIABendPoints);
      this.inflateBendPoints(FMBendPoints);
    }

    // Take up to the  last 35 years and compute PIA and get family maximum
    let totalIndexedAmount = 0;
    let totalMonths = 0;

    for (let ix = earningsHistory.length - 1; ix >= Math.max(0, earningsHistory.length - 35); --ix) {
      totalIndexedAmount += earningsHistory[ix];
      totalMonths += 12;
    }

    let PIA = this.getWithBendPoints(
      totalMonths ? totalIndexedAmount / totalMonths : 0,
      PIABendPoints,
      [0.9, 0.32, 0.15]
    );
    const familyMaximum = this.getWithBendPoints(PIA, FMBendPoints, [1.5, 2.72, 1.34, 1.75]);

    // Go forward to retirement if you ain't dead
    if (deceased !== this.applicant) {
      currentYear = new Date().getFullYear() + yearOfDeath;

      const retirementYear = currentYear + Math.max(0, retirementAge - age);

      while (++currentYear < retirementYear) {
        earningsHistory.push(
          Math.min(this.customer.assumptions.ssnTable[currentYear][0], earnings) *
            this.customer.assumptions.ssnTable[currentYear][1]
        );
        earnings = earnings + earnings * this.customer.settings.inflation;
        this.inflateBendPoints(PIABendPoints);
      }
    }

    // Take last 35 years to get benefit amount at retirement (PIA)
    totalIndexedAmount = 0;
    totalMonths = 0;

    for (let ix = earningsHistory.length - 1; ix >= Math.max(0, earningsHistory.length - 35); --ix) {
      totalIndexedAmount += earningsHistory[ix];
      totalMonths += 12;
    }

    PIA = this.getWithBendPoints(totalMonths ? totalIndexedAmount / totalMonths : 0, PIABendPoints, [0.9, 0.32, 0.15]);

    return {
      amount: PIA,
      startYear: this.getStartYear() + (yearOfDeath ? yearOfDeath : 0),
      endYear: this.getEndYear(),
      age: age,
      retirementAge: this.customer.assumptions.SSNAge,
      earnedIncome: survivorEarnedIncome,
      earnedIncomeFlow: survivorEarnedIncome.getStartingFlow(deceased),
      familyMaximum: familyMaximum,
    };
  }

  getDescription() {
    return this.earnedIncome.applicant === this.customer.primaryCustomer ? 'Your SSN' : 'Spouse SSN';
  }

  inflateBendPoints(points) {
    for (let ix = 0; ix < points.length; ++ix) {
      points[ix] += points[ix] * this.customer.assumptions.ssnCOLA;
    }
  }

  getWithBendPoints(amount, points, percent) {
    let sum = 0;
    let point = 0;

    for (let ix = 0; ix < percent.length; ++ix) {
      sum += Math.max(0, Math.min(points[ix] ? points[ix] : 99999999, amount) - point) * percent[ix];
      point = points[ix];
    }

    return sum;
  }

  getAmount(flow, year?, deceased?, yearOfDeath?) {
    if (year < yearOfDeath) {
      return 0;
    }

    const retirementBenefit = flow.age + year < flow.retirementAge ? 0 : flow.amount;
    let familyBenefit;

    // If deceased determine survivor benefits http://www.ssa.gov/pubs/10084.html#a0=2
    const survivor =
      deceased === this.customer.primaryCustomer ? this.customer.alternateCustomer : this.customer.primaryCustomer;

    if (deceased === this.earnedIncome.applicant) {
      // Find total children under the age of 19
      let kidsUnder19 = 0;

      for (let ix = 0; ix < this.customer.capitalNeeds.children.length; ++ix) {
        if (year + this.customer.capitalNeeds.children[ix].age < 19) {
          kidsUnder19++;
        }
      }

      // Find total children under the age of 19
      let kidsUnder16 = 0;

      for (let ix = 0; ix < this.customer.capitalNeeds.children.length; ++ix) {
        if (year + this.customer.capitalNeeds.children[ix].age < 16) {
          kidsUnder16++;
        }
      }

      // 75% of benefit for each kid under 19
      const childBenefit = flow.amount * 0.75 * kidsUnder19;

      // When you turn 60 or more you are eligible for a survivor benefit that is prorated 7% for each year you retire
      // before the social security retirement age
      const yearEligibleForSurvivorBenefit = Math.max(0, 60 - survivor.person.age);
      const ageStartCollectingSurvivorBenefit = yearEligibleForSurvivorBenefit + survivor.person.age;
      const earlySurvivorBenefitReduction =
        Math.max(0, this.customer.assumptions.SSNAge - ageStartCollectingSurvivorBenefit) * 0.07;
      let survivorSpousalBenefit =
        this.customer.profile.relationship === 'married' && year >= yearEligibleForSurvivorBenefit
          ? flow.amount - flow.amount * earlySurvivorBenefitReduction
          : 0;

      // With dids under 16 you get at least 75% of your benefit no matter what age
      const survivorBenefitWithYoungKids =
        this.customer.profile.relationship === 'married' && kidsUnder16 ? flow.amount * 0.75 : 0;

      survivorSpousalBenefit += survivorBenefitWithYoungKids;

      // Reduce amount by 50 cents of every dollar of income over threshold for survivor (but not kids)
      let income = flow.earnedIncome.getAmount(flow.earnedIncomeFlow, year, deceased);

      income = Math.max(0, income - this.customer.assumptions.ssnMaxIncome[year + new Date().getFullYear()]);
      survivorSpousalBenefit = Math.max(0, survivorSpousalBenefit - income / 2);

      const survivorEarnedIncome =
        deceased === this.customer.primaryCustomer
          ? this.customer.capitalNeeds.earnedIncome[1]
          : this.customer.capitalNeeds.earnedIncome[0];

      // Family maximum is between 160% and 180% of deceased benefit http://www.ssa.gov/oact/cola/familymax.html
      /*
             - If spouse works full time do not include ssn survivor income for spouse in model (but do include children one).
             - If spouse does not work full time do include ssn survivor income for spouse in model (as well as children survivor)
             - Do not include SSN retirement in any case

             Note if you are single with kids then your kids get the survivor benefits
             */
      if (!survivorEarnedIncome.works || this.customer.assumptions.useSSNSurvirorBenefits) {
        familyBenefit = Math.min(survivorSpousalBenefit + childBenefit, flow.familyMaximum);
      } else {
        familyBenefit = Math.min(childBenefit, flow.familyMaximum);
      }

      return familyBenefit * 12;
    } else {
      return retirementBenefit * (1 - this.taxRate) * 12;
    }
  }

  adjustFlow(flow) {
    flow.familyMaximum += flow.familyMaximum * this.customer.assumptions.ssnCOLA;
    flow.amount += flow.amount * this.customer.assumptions.ssnCOLA;
    flow.earnedIncome.adjustFlow(flow.earnedIncomeFlow);
  }
}
